/**
 * D6 产品库直连:租户产品注册表节点 → 阶段一规划入参。
 *
 * 红线:**一个参数都不许编**。字段一律取 `GET /tenant/products` 的原文
 * (name / summary / sellingPoints / attributes),取不到就留空 —— 留空会让规划
 * 少写一块,编一个会让成图上出现租户没说过的话,后者严重得多。
 *
 * 这是长图「不知所云」的正解:用户手工搭建时面对的是 `defaultPropsFor` 的占位值
 * (「名称/内容」「99%/指标名」「本品 3 / 传统 30」),不改就出图,观感上等同乱编。
 */
import type { ProductNode } from "@/entities/product";
import type { CanvasPlanRequest, CanvasProductSpec } from "./canvas";

export type PlanProductInput = CanvasPlanRequest["product"];

/**
 * 这条 attribute 能不能拿去画面上用。
 *
 * ★判据单独导出:波B 的 `free-create/productAdapter.ts` 要用**同一条规则**
 * (对象/数组是内部结构,不往画面上放),但两边的长度上限不同 ——
 * 规划入参有 ai schema 的 max_length,自由创作那条只有 rawPrompt 总长度闸门。
 * 规则单源、上限各自管,才不会出现"一边放行一边拦"的漂移。
 */
export function isDisplayableAttribute(raw: unknown): raw is string | number {
  return typeof raw === "string" || typeof raw === "number";
}

/** attributes 里能安全当规格用的标量(对象/数组是内部结构,不往画面上放)。 */
function scalarAttributes(attrs: Record<string, unknown> | null | undefined): CanvasProductSpec[] {
  if (!attrs) return [];
  const out: CanvasProductSpec[] = [];
  for (const [label, raw] of Object.entries(attrs)) {
    if (!isDisplayableAttribute(raw)) continue;
    const value = String(raw).trim();
    if (!label.trim() || !value) continue;
    out.push({ label: label.trim().slice(0, 40), value: value.slice(0, 120), unit: "" });
    if (out.length >= 16) break; // 与 ai PlanProduct.specs max_length=30 同侧留余量
  }
  return out;
}

// 「可选产品」判据 2026-08-10 提升到 entities/product/lib/pickable(数字人「适配我的产品」
// 第三个消费方进场,三处必须同一份口径);这里 re-export 保住既有 import 路径。
// ★直连具体文件不走 barrel:好几个页面测试把 "@/entities/product" 整包 mock 了
//   (ReporterCreatePage 等),走 barrel 会让那些测试炸「No pickableProducts export」。
export { pickableProducts } from "@/entities/product/lib/pickable";

/**
 * 产品节点 → 规划入参。`allNodes` 只用来把 parent code 翻成父节点**原名**当类目,
 * 翻不到就留空(不猜类目)。
 */
export function planProductFromNode(
  node: ProductNode,
  allNodes: ProductNode[] = []
): PlanProductInput {
  const parent = node.parent ? allNodes.find((n) => n.code === node.parent) : undefined;
  return {
    name: node.name.trim().slice(0, 60),
    category: (parent?.name ?? "").trim().slice(0, 30) || undefined,
    // 产品库没有品牌字段 —— 不编,留空由用户自己填。
    brand: undefined,
    sellingPoints: (node.sellingPoints ?? [])
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 12),
    specs: scalarAttributes(node.attributes),
    specsText: (node.summary ?? "").trim().slice(0, 1000) || undefined,
  };
}

/** 规格清单 → 「规格参数」文本框回显(用户可继续手改;仍是原文逐字)。 */
export function specsToText(specs: CanvasProductSpec[]): string {
  return specs.map((s) => `${s.label} ${s.value}`.trim()).join("\n");
}

/** ai `ProductSpec` schema 上限(canvas.py:79)——反解时就地截断,别让请求在 ai 侧 422。 */
const SPEC_LABEL_MAX = 40;
const SPEC_VALUE_MAX = 120;
/** ai `PlanProduct.specs` max_length=30。 */
const SPEC_ITEMS_MAX = 30;

/**
 * 「规格参数」文本框 → 结构化规格清单({@link specsToText} 的逆运算)。
 *
 * ★为什么必须有这一步(D11,2026-07-29 走查):spec 区块此前**必然**渲染成空表。
 * 三处都按「有结构化 specs」设计,唯独入口没给 ——
 *  ① 面板选产品时把结构化规格拍成自由文本,提交只发 `specsText`;
 *  ② ai `_plan_prompt` 只在 `product.specs` 非空时才输出带下标的【规格清单】,
 *     没下标 → 规划 LLM 产不出 `dataPointBinds` → 规格落进 `bullets`;
 *  ③ `canvasBridge` pairs 分支读 `dataPoints`,空 → 占位「名称/内容」。
 *
 * **反解而不是「记住选产品时的那一份」** —— 文本框用户改得动,记住的那份改一下就过期;
 * 反解还顺带兑现了 UI 上写的「规格参数 · 自由粘贴,AI 自动结构化」。
 *
 * 红线不变:**一个参数都不许编**。只做切分与截断,分不出 label/value 的行**跳过**
 * (不硬凑一个 value 出来)—— 反正 `specsText` 原文仍然全量上传,这里少一条只是
 * 少一个可绑定下标,不会丢信息。
 */
export function parseSpecsText(text: string): CanvasProductSpec[] {
  const out: CanvasProductSpec[] = [];
  for (const line of (text ?? "").split("\n")) {
    const s = line.trim();
    if (!s) continue;
    // 冒号优先(用户手粘常写「净含量:300ml」),否则退回首个空白 —— 后者是 specsToText 的写法
    const m = /^([^:：]+)[:：]\s*(.+)$/.exec(s) ?? /^(\S+)\s+(.+)$/.exec(s);
    if (!m) continue;
    const label = m[1].trim().slice(0, SPEC_LABEL_MAX);
    const value = m[2].trim().slice(0, SPEC_VALUE_MAX);
    if (!label || !value) continue;
    out.push({ label, value, unit: "" });
    if (out.length >= SPEC_ITEMS_MAX) break;
  }
  return out;
}

/**
 * canvas 里的产品 → ai 规划入参(`PlanProduct`)的形状。
 *
 * 用在「让 AI 把这一段写出来」:那一跳要拿**已有事实**写,而事实此刻只活在 canvas 里。
 * ★与 `planProductFromNode` 同一个目的地,只是来源不同 —— 两个都往这一个形状收。
 */
export function planProductFromCanvas(p: {
  name: string;
  category: string;
  brand: string;
  sellingPoints: Array<{ text: string }>;
  specs: Array<{ label: string; value: string; unit: string }>;
  price: { now: string; old: string };
}): Record<string, unknown> {
  return {
    name: p.name,
    category: p.category,
    brand: p.brand,
    sellingPoints: p.sellingPoints.map((sp) => sp.text).filter(Boolean),
    specs: p.specs,
    priceNow: p.price.now,
    priceOld: p.price.old,
  };
}
